/**
 * ProxyConfig Component
 *
 * Configuration component for proxy/browser location settings.
 * Allows users to select ISP or Residential proxies and choose
 * specific countries and cities for browser location.
 *
 * @param {Object} props
 * @param {Object} props.config - Current configuration values
 * @param {Function} props.onChange - Called with new config on any change
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import { HiGlobeAlt, HiShieldCheck } from "react-icons/hi2";
import { FiSearch, FiChevronDown } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import cloudApi from "../../../../../services/cloudApi";
import { getCountryEmoji, formatCityName } from "../../utils/proxyHelpers";

const PROXY_PREFS_KEY = "cloudProxyUserPrefs";
const PROXY_ONBOARDING_KEY = "cloudProxyOnboardingDismissed";

const ProxyConfig = ({ config, onChange }) => {
  const [locations, setLocations] = useState({ isp: null, residential: null, cities: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countrySearch, setCountrySearch] = useState("");
  const [citySearch, setCitySearch] = useState("");
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const countryDropdownRef = useRef(null);
  const cityDropdownRef = useRef(null);

  // Check if onboarding was dismissed
  useEffect(() => {
    try {
      if (!localStorage.getItem(PROXY_ONBOARDING_KEY)) {
        setShowOnboarding(true);
      }
    } catch (err) {
      console.error("Failed to check proxy onboarding state:", err);
    }
  }, []);

  const dismissOnboarding = () => {
    setShowOnboarding(false);
    try {
      localStorage.setItem(PROXY_ONBOARDING_KEY, "true");
    } catch (err) {
      console.error("Failed to save proxy onboarding dismissal:", err);
    }
  };

  // Fetch locations when component mounts (uses cloudApi 24h cache)
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await cloudApi.getProxyLocations({ type: "all", includeCities: true });

        const ispCountries = data.isp?.countries || [];
        const residentialCountries = data.residential?.countries || [];
        const cities = data.residential?.cities || {};
        const totalCities = Object.values(cities).reduce((sum, arr) => sum + arr.length, 0);

        console.log("📍 [ProxyConfig] Locations loaded:", {
          ispCountries: ispCountries.length,
          residentialCountries: residentialCountries.length,
          countriesWithCities: Object.keys(cities).length,
          totalCities: totalCities,
        });

        if (data.suggestedCountry) {
          console.log("🎯 [ProxyConfig] Suggested country from API:", {
            name: data.suggestedCountry.name,
            code: data.suggestedCountry.code,
          });
        }

        setLocations({
          isp: ispCountries,
          residential: residentialCountries,
          cities: cities,
        });
      } catch (err) {
        console.error("❌ [ProxyConfig] Failed to fetch proxy locations:", err);
        setError("Failed to load proxy locations");
      } finally {
        setLoading(false);
      }
    };
    fetchLocations();
  }, []);

  // Close country dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setCountryDropdownOpen(false);
      }
    };

    if (countryDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [countryDropdownOpen]);

  // Close city dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
        setCityDropdownOpen(false);
      }
    };

    if (cityDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [cityDropdownOpen]);

  // Force residential to isp if somehow saved in prefs (residential is temporarily disabled)
  useEffect(() => {
    if (config.proxyType === "residential") {
      const newConfig = { ...config, proxyType: "isp", city: null };
      onChange(newConfig);
      saveUserPrefs(newConfig);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Save user preferences to localStorage
  const saveUserPrefs = (newConfig) => {
    try {
      localStorage.setItem(PROXY_PREFS_KEY, JSON.stringify(newConfig));
    } catch (err) {
      console.error("Failed to save proxy preferences:", err);
    }
  };

  const handleProxyTypeChange = (type) => {
    // Residential proxies are temporarily disabled
    if (type === "residential") return;

    // Reset country and city when switching proxy types
    const newConfig = {
      ...config,
      proxyType: type,
      country: null,
      city: null,
    };
    onChange(newConfig);
    saveUserPrefs(newConfig);
    setCountrySearch("");
    setCitySearch("");
  };

  const handleCountrySelect = (country) => {
    const newConfig = {
      ...config,
      country: country,
      city: null, // Reset city when country changes
    };
    onChange(newConfig);
    saveUserPrefs(newConfig);
    setCountryDropdownOpen(false);
    setCountrySearch("");
    setCitySearch("");
  };

  const handleCitySelect = (city) => {
    const newConfig = { ...config, city };
    onChange(newConfig);
    saveUserPrefs(newConfig);
    setCityDropdownOpen(false);
    setCitySearch("");
  };

  // Get countries based on selected proxy type
  const countries = useMemo(() => {
    return config.proxyType === "isp" ? locations.isp : locations.residential;
  }, [config.proxyType, locations]);

  // Filter countries based on search
  const filteredCountries = useMemo(() => {
    if (!countries) return [];
    if (!countrySearch.trim()) return countries;
    const search = countrySearch.toLowerCase();
    return countries.filter(
      (c) =>
        c.name.toLowerCase().includes(search) ||
        c.code.toLowerCase().includes(search)
    );
  }, [countries, countrySearch]);

  // Get cities for selected country
  const availableCities = useMemo(() => {
    if (!config.country?.code) return [];
    return locations.cities[config.country.code] || [];
  }, [config.country?.code, locations.cities]);

  // Filter cities based on search
  const filteredCities = useMemo(() => {
    if (!availableCities.length) return [];
    if (!citySearch.trim()) return availableCities;
    const search = citySearch.toLowerCase();
    return availableCities.filter((city) =>
      formatCityName(city).toLowerCase().includes(search)
    );
  }, [availableCities, citySearch]);

  const showCitySelector = config.proxyType === "residential" && config.country !== null;

  if (loading) {
    return (
      <div className="space-y-2.5">
        <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 dark:border-blue-400"></div>
            <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading locations...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-2.5">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 rounded-2xl p-4">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {/* Onboarding Illustration */}
      {showOnboarding && (
        <div className="relative rounded-xl overflow-hidden border border-blue-200 dark:border-blue-500/20 bg-gradient-to-br from-white dark:from-gray-900/80 via-gray-50 dark:via-gray-900/70 to-blue-50 dark:to-blue-950/40 backdrop-blur-md shadow-xl">
          {/* Close button */}
          <button
            onClick={dismissOnboarding}
            className="absolute top-2 right-2 z-10 w-6 h-6 rounded-lg flex items-center justify-center
                       bg-gray-100 dark:bg-gray-800/60 border border-gray-300 dark:border-gray-600/40 hover:bg-gray-200 dark:hover:bg-gray-700/80 hover:border-gray-400 dark:hover:border-gray-500/50
                       transition-all duration-200"
          >
            <IoClose className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" />
          </button>

          <div className="px-4 pt-4 pb-3">
            {/* Illustration */}
            <div className="flex justify-center mb-3">
              <div className="relative">
                <div className="absolute inset-0 bg-blue-500/10 rounded-full blur-2xl scale-150" />
                {/* Mini browser frame */}
                <div className="relative w-52 h-36 rounded-xl bg-white dark:bg-gray-900/80 border border-gray-300 dark:border-gray-600/40 shadow-xl overflow-hidden flex flex-col">
                  {/* Browser toolbar */}
                  <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 dark:bg-gray-800/90 border-b border-gray-200 dark:border-gray-700/50">
                    <div className="flex gap-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-400/60" />
                      <div className="w-1.5 h-1.5 rounded-full bg-yellow-400/60" />
                      <div className="w-1.5 h-1.5 rounded-full bg-green-400/60" />
                    </div>
                    <div className="flex-1 flex items-center gap-1 mx-1 px-2 py-0.5 bg-gray-200 dark:bg-gray-700/50 rounded-md">
                      <HiGlobeAlt className="w-2 h-2 text-blue-400/70" />
                      <div className="w-14 h-1 bg-gray-300 dark:bg-gray-500/30 rounded-full" />
                    </div>
                  </div>
                  {/* Page content — proxy location overlay */}
                  <div className="relative flex-1 p-2">
                    {/* Background "page" content lines */}
                    <div className="space-y-1.5 opacity-40">
                      <div className="w-full h-1 bg-gray-300 dark:bg-gray-600/40 rounded-full" />
                      <div className="w-3/4 h-1 bg-gray-300 dark:bg-gray-600/40 rounded-full" />
                      <div className="w-full h-1 bg-gray-300 dark:bg-gray-600/40 rounded-full" />
                      <div className="w-2/3 h-1 bg-gray-300 dark:bg-gray-600/40 rounded-full" />
                      <div className="w-5/6 h-1 bg-gray-300 dark:bg-gray-600/40 rounded-full" />
                    </div>

                    {/* Proxy location card — centered overlay */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-white dark:bg-gray-800/95 border border-blue-300 dark:border-blue-500/30 rounded-lg px-3 py-2 shadow-lg">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-500/20 border border-blue-300 dark:border-blue-400/30 flex items-center justify-center">
                            <HiGlobeAlt className="w-3 h-3 text-blue-500 dark:text-blue-300" />
                          </div>
                          <div>
                            <div className="text-[7px] text-gray-400 dark:text-gray-500 mb-0.5">Browsing from</div>
                            <div className="flex items-center gap-1">
                              <span className="text-[9px]">🇺🇸</span>
                              <span className="text-[8px] text-blue-600 dark:text-blue-200 font-medium">New York, US</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Subtle shimmer */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/5 to-transparent animate-pulse" />
                  </div>
                </div>

                {/* Shield badge */}
                <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-blue-500/90 border-2 border-white dark:border-gray-900 flex items-center justify-center shadow-md">
                  <HiShieldCheck className="w-2.5 h-2.5 text-white" />
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="text-center px-2">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">Browse From Any Location</h3>
              <p className="text-xs text-gray-500 leading-relaxed">
                Route your cloud browser through residential or ISP proxies worldwide. Choose a specific country and city for authentic local access.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Proxy Settings Section */}
      <div
        className={`bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm ${countryDropdownOpen || cityDropdownOpen ? "relative z-50" : ""}`}
      >
        <div className="flex items-center mb-3 pb-2 border-b border-gray-200 dark:border-gray-700/30">
          <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-500/15 mr-2">
            <HiGlobeAlt className="text-blue-500 dark:text-blue-400 w-3.5 h-3.5" />
          </div>
          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide">Proxy Settings</h3>
        </div>

        <div className="space-y-3">
          {/* Proxy Type */}
          <div className="py-1.5 px-2 rounded-lg">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Proxy Type</label>
            <div className="flex flex-col gap-2">
              {/* ISP Option */}
              <button
                onClick={() => handleProxyTypeChange("isp")}
                className={`relative w-full text-left p-3 pl-10 rounded-xl transition-all duration-200 ${
                  config.proxyType === "isp"
                    ? "bg-blue-50 dark:bg-blue-500/15 border border-blue-400 dark:border-blue-500/50 shadow-sm"
                    : "bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/40 hover:border-gray-300 dark:hover:border-gray-600/60 hover:bg-gray-100 dark:hover:bg-gray-800/60"
                }`}
              >
                {/* Radio indicator */}
                <div
                  className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                    config.proxyType === "isp" ? "border-blue-500 dark:border-blue-400 bg-blue-100 dark:bg-blue-500/20" : "border-gray-400 dark:border-gray-500 bg-transparent"
                  }`}
                >
                  {config.proxyType === "isp" && <div className="w-2 h-2 rounded-full bg-blue-500 dark:bg-blue-400" />}
                </div>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-sm font-medium ${config.proxyType === "isp" ? "text-gray-900 dark:text-white" : "text-gray-700 dark:text-gray-300"}`}>🌐 Standard (ISP)</span>
                  <span className="text-2xs px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/30">No additional cost</span>
                </div>
                <p className={`text-2xs leading-relaxed ${config.proxyType === "isp" ? "text-gray-600 dark:text-gray-300" : "text-gray-500"}`}>
                  Works well with many websites. May be blocked by some major websites. Country only.
                </p>
              </button>

              {/* Residential Option (Coming Soon) */}
              <div
                className="relative w-full text-left p-3 pl-10 rounded-xl transition-all duration-200
                           bg-gray-50 dark:bg-gray-800/20 border border-gray-200 dark:border-gray-700/20 opacity-60 cursor-not-allowed"
              >
                {/* Radio indicator */}
                <div
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 transition-all duration-200 flex items-center justify-center border-gray-300 dark:border-gray-600 bg-transparent"
                >
                </div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-400 dark:text-gray-500">🏠 Premium (Residential)</span>
                  <span className="text-2xs px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-600/30 text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-600/30">Coming Soon</span>
                </div>
                <p className="text-2xs leading-relaxed text-gray-400 dark:text-gray-600">
                  Less likely to be blocked by major websites. Choose country and city.
                </p>
              </div>
            </div>
          </div>

          {/* Country Selection */}
          <div className="py-1.5 px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Country</label>
            <div className="relative" ref={countryDropdownRef}>
              <button
                onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                className="w-full flex items-center justify-between bg-white dark:bg-gray-700/60 text-gray-900 dark:text-white px-3 py-2 rounded-lg text-sm
                           border border-gray-300 dark:border-gray-600/50 hover:border-gray-400 dark:hover:border-gray-500/50 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none transition-colors"
              >
                <span className="flex items-center gap-2">
                  {config.country ? (
                    <>
                      <span>{getCountryEmoji(config.country.code)}</span>
                      <span>{config.country.name}</span>
                    </>
                  ) : (
                    <span className="text-gray-400">Random</span>
                  )}
                </span>
                <FiChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${countryDropdownOpen ? "rotate-180" : ""}`} />
              </button>

              {countryDropdownOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-64 overflow-hidden">
                  {/* Search input */}
                  <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                    <div className="relative">
                      <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                      <input
                        type="text"
                        value={countrySearch}
                        onChange={(e) => setCountrySearch(e.target.value)}
                        placeholder="Search countries..."
                        className="w-full bg-gray-50 dark:bg-gray-700/60 text-gray-900 dark:text-white pl-8 pr-3 py-1.5 rounded-md text-sm
                                   border border-gray-200 dark:border-gray-600/50 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none placeholder-gray-400 dark:placeholder-gray-500"
                        autoFocus
                      />
                    </div>
                  </div>

                  {/* Options list */}
                  <div className="overflow-y-auto max-h-48">
                    {/* Random option */}
                    <button
                      onClick={() => handleCountrySelect(null)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                        config.country === null ? "bg-blue-50 dark:bg-blue-500/20 text-blue-600 dark:text-blue-300" : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50"
                      }`}
                    >
                      <span>Random</span>
                    </button>

                    {/* Country options */}
                    {filteredCountries.map((country) => (
                      <button
                        key={country.code}
                        onClick={() => handleCountrySelect(country)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                          config.country?.code === country.code ? "bg-blue-50 dark:bg-blue-500/20 text-blue-600 dark:text-blue-300" : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50"
                        }`}
                      >
                        <span>{getCountryEmoji(country.code)}</span>
                        <span>{country.name}</span>
                      </button>
                    ))}

                    {filteredCountries.length === 0 && countrySearch && <div className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">No countries found</div>}
                  </div>
                </div>
              )}
            </div>
            <p className="text-2xs text-gray-400 dark:text-gray-500 mt-2">{config.country ? `Browser will appear from ${config.country.name}` : "Browser location will be randomly selected"}</p>
          </div>

          {/* City Selection (Residential only, when country selected) */}
          {showCitySelector && (
            <div className="py-1.5 px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">City</label>
              <div className="relative" ref={cityDropdownRef}>
                <button
                  onClick={() => setCityDropdownOpen(!cityDropdownOpen)}
                  className="w-full flex items-center justify-between bg-white dark:bg-gray-700/60 text-gray-900 dark:text-white px-3 py-2 rounded-lg text-sm
                             border border-gray-300 dark:border-gray-600/50 hover:border-gray-400 dark:hover:border-gray-500/50 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none transition-colors"
                >
                  <span>{config.city ? formatCityName(config.city) : <span className="text-gray-400">Any city</span>}</span>
                  <FiChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${cityDropdownOpen ? "rotate-180" : ""}`} />
                </button>

                {cityDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-64 overflow-hidden">
                    {/* Search input */}
                    {availableCities.length > 5 && (
                      <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                        <div className="relative">
                          <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                          <input
                            type="text"
                            value={citySearch}
                            onChange={(e) => setCitySearch(e.target.value)}
                            placeholder="Search cities..."
                            className="w-full bg-gray-50 dark:bg-gray-700/60 text-gray-900 dark:text-white pl-8 pr-3 py-1.5 rounded-md text-sm
                                       border border-gray-200 dark:border-gray-600/50 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none placeholder-gray-400 dark:placeholder-gray-500"
                            autoFocus
                          />
                        </div>
                      </div>
                    )}

                    {/* Options list */}
                    <div className="overflow-y-auto max-h-48">
                      {/* Any city option */}
                      <button
                        onClick={() => handleCitySelect(null)}
                        className={`w-full px-3 py-2 text-sm text-left transition-colors ${config.city === null ? "bg-blue-50 dark:bg-blue-500/20 text-blue-600 dark:text-blue-300" : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50"}`}
                      >
                        Any city
                      </button>

                      {/* City options */}
                      {filteredCities.map((city) => (
                        <button
                          key={city}
                          onClick={() => handleCitySelect(city)}
                          className={`w-full px-3 py-2 text-sm text-left transition-colors ${config.city === city ? "bg-blue-50 dark:bg-blue-500/20 text-blue-600 dark:text-blue-300" : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50"}`}
                        >
                          {formatCityName(city)}
                        </button>
                      ))}

                      {filteredCities.length === 0 && citySearch && <div className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">No cities found</div>}

                      {availableCities.length === 0 && !citySearch && <div className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">Loading cities...</div>}
                    </div>
                  </div>
                )}
              </div>
              <p className="text-2xs text-gray-400 dark:text-gray-500 mt-2">{config.city ? `Proxy will be from ${formatCityName(config.city)}` : "Any city in the selected country"}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProxyConfig;
